import { injectable, inject } from '@patsnap/tahiti-core/shared/inversify';
import { createDialog } from '@patsnap/tahiti-core';
import type { FreeTrailDialogService, FreeTrailDialogOptions } from '../free-trail-dialog.service';
import { AddProApi } from '@telecom/shared/domain/permission';

const STORAGE_KEY = 'eureka_free_trail_dialog_shown';

/**
 * 免费试用对话框服务实现
 * @description 管理免费试用对话框的显示，支持只显示一次的功能
 */
@injectable()
export class FreeTrailDialogServiceImpl implements FreeTrailDialogService {
    private _dialogFactory = createDialog(() => import('../FreeTrailDialog.vue'));

    constructor(
        @inject(AddProApi)
        private _addProApi: AddProApi,
    ) {}

    /**
     * 显示对话框
     * @param options - 对话框选项
     * @returns Promise<boolean> - true: 用户确认, false: 用户取消
     */
    async show(options?: FreeTrailDialogOptions): Promise<boolean> {
        const dialog = this._dialogFactory();
        return dialog.open(options);
    }

    /**
     * 只显示一次对话框，如果已经显示过则不再显示
     * @param options - 对话框选项
     * @returns Promise<boolean> - true: 用户确认, false: 用户取消或已显示过
     */
    async showOnce(options?: FreeTrailDialogOptions): Promise<boolean> {
        if (this.hasShown()) {
            return false;
        }
        this._setShown();
        const result = await this.show(options);
        return result;
    }

    /**
     * 重置显示状态，下次调用 showOnce 时会再次显示
     */
    reset(): void {
        try {
            localStorage.removeItem(STORAGE_KEY);
        } catch (error) {
            console.warn('Failed to reset free trail dialog state:', error);
        }
    }

    /**
     * 检查是否已经显示过
     * @returns boolean - true: 已显示过, false: 未显示过
     */
    hasShown(): boolean {
        try {
            const shown = localStorage.getItem(STORAGE_KEY);
            return shown === 'true';
        } catch (error) {
            console.warn('Failed to check free trail dialog state:', error);
            return false;
        }
    }

    /**
     * 标记对话框为已显示
     */
    private _setShown(): void {
        try {
            localStorage.setItem(STORAGE_KEY, 'true');
        } catch (error) {
            console.warn('Failed to set free trail dialog state:', error);
        }
    }

    /**
     * 激活 Pro 套餐
     * @description 调用 API 激活 Pro 套餐，成功后清除缓存并重新加载页面
     */
    async activateProPkg(): Promise<void> {
        try {
            await this._addProApi.addProPkg();

            // 清除缓存
            localStorage.removeItem('patsnap_header_account_info');
            // 清除token,强制让其去获取新的token
            localStorage.removeItem('patsnap-authorizations');

            // 重新加载页面
            window.location.reload();
        } catch (error) {
            console.error('[ActivateProPkg]', error);
            throw error;
        }
    }
}
